import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Lock, Camera, Eye, EyeOff, Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { uploadService } from '@/services/upload.service';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

export function ProfileModal({
  open,
  onClose,
}: ProfileModalProps) {
  const { t } = useTranslation('common');
  const { user, updateUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'info' | 'password'>('info');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setActiveSection('info');
      setAvatarPreview(null);
    }
  }, [open, user]);

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  const handleAvatarFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = uploadService.validateImage(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Preview local imediato
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);

    try {
      setUploadingAvatar(true);
      const signedUrl = await uploadService.uploadAvatarViaBackend(file);
      updateUser({ avatar_url: signedUrl });
      toast.success('Foto de perfil atualizada');
    } catch {
      toast.error('Erro ao fazer upload da foto de perfil');
      setAvatarPreview(null);
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploadingAvatar(true);
      await uploadService.deleteAvatarViaBackend();
      updateUser({ avatar_url: null });
      setAvatarPreview(null);
      toast.success('Foto de perfil removida');
    } catch {
      toast.error('Erro ao remover foto de perfil');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveInfo = async () => {
    if (!name.trim()) {
      toast.error(t('profile.name_required'));
      return;
    }

    if (!email.trim()) {
      toast.error(t('profile.email_required'));
      return;
    }

    try {
      setSaving(true);
      const response = await api.patch('/auth/profile', {
        name: name.trim(),
        email: email.trim(),
      });

      // Atualiza o contexto com os novos dados
      if (response.data?.user) {
        updateUser({
          name: response.data.user.name,
          email: response.data.user.email,
        });
      }

      toast.success(t('profile.profile_updated'));
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || t('profile.error_updating'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error(t('profile.current_password_required'));
      return;
    }

    if (!newPassword) {
      toast.error(t('profile.new_password_required'));
      return;
    }

    if (newPassword.length < 6) {
      toast.error(t('profile.password_min_length'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('profile.passwords_not_match'));
      return;
    }

    try {
      setSaving(true);
      await api.patch('/auth/password', {
        currentPassword,
        newPassword,
      });
      toast.success(t('profile.password_changed'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || t('profile.error_changing_password'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            {t('profile.title')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('profile.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Avatar */}
          <div className="flex justify-center">
            <div className="relative group">
              <Avatar className="h-20 w-20 border-4 border-border">
                {(avatarPreview || user?.avatar_url) ? (
                  <AvatarImage src={avatarPreview ?? user?.avatar_url ?? undefined} alt={user?.name} />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                  {uploadingAvatar ? (
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  ) : (
                    getUserInitials()
                  )}
                </AvatarFallback>
              </Avatar>

              {/* Input oculto */}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarFileSelect}
                disabled={uploadingAvatar}
              />

              {/* Botão de upload sobreposto */}
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
                title="Alterar foto de perfil"
              >
                <Camera className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Botão remover avatar (visível só quando há foto) */}
            {(avatarPreview || user?.avatar_url) && !uploadingAvatar && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                className="absolute mt-16 ml-16 p-1 rounded-full bg-destructive text-destructive-foreground shadow hover:bg-destructive/90 transition-colors"
                title="Remover foto de perfil"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setActiveSection('info')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeSection === 'info'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('profile.tab_info')}
            </button>
            <button
              onClick={() => setActiveSection('password')}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeSection === 'password'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('profile.tab_password')}
            </button>
          </div>

          {activeSection === 'info' ? (
            /* Seção de Informações */
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="profile-name" className="text-sm">
                  {t('profile.name')}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('profile.name_placeholder')}
                    className="pl-9"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="profile-email" className="text-sm">
                  {t('profile.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="profile-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-9"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Seção de Senha */
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="current-password" className="text-sm">
                  {t('profile.current_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('profile.current_password_placeholder')}
                    className="pl-9 pr-9"
                    disabled={saving}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-sm">
                  {t('profile.new_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('profile.new_password_placeholder')}
                    className="pl-9 pr-9"
                    disabled={saving}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('profile.min_chars')}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-sm">
                  {t('profile.confirm_password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('profile.confirm_password_placeholder')}
                    className="pl-9 pr-9"
                    disabled={saving}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            size="sm"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={activeSection === 'info' ? handleSaveInfo : handleChangePassword}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-foreground mr-1.5"></div>
                {t('common.saving')}
              </>
            ) : activeSection === 'info' ? (
              t('common.save')
            ) : (
              t('profile.change_password')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
